import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BillsTable from '../components/BillsTable';
import FlashMessage from '../components/FlashMessage';
import { DateInputField, FormRow, FormWrapper, SubmitButton, PageContainer, ContentContainer, Header, AddButton, Label } from '../styles/ComponentStyles';
import { StatusSelect, BillNumberInput, HelperText } from '../styles/BillsFormStyles';
import { BillersDropdown, BillPaymentTypesDropdown, BillTypeDropdown } from '../components/Dropdowns';
import apiService from '../services/apiService';
import PaymentsModal from '../components/PaymentsModal';
import { API_URL } from '../config';

const Bills = ({ isSidebarOpen }) => {
  const [totalItems, setTotalItems] = useState([]);
  const [totalPages, setTotalPages] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageLimit, setCurrentPageLimit] = useState(100);
  const navigate = useNavigate();
  const [flashMessage, setFlashMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);

  const [formData, setFormData] = useState({
    biller_id: '',
    biller: '',
    bill_type: '',
    bill_payment_type: '',
    amount: '',
    due_date: '',
    platform: '',
    store: '',
    remarks: '',
    payments: [],
    status: 'active',
    payment_frequency: 'one_time',
    due_months: '',
  });

  const refreshData = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleFlashMessage = (message) => {
    setFlashMessage(message);
    setTimeout(() => {
      setFlashMessage('');
    }, 3000);
  };

  useEffect(() => {
    const fetchBills = async () => {
      const result = await apiService.getBills(currentPage, currentPageLimit);
      setTotalItems(result.data.total_items || 0);
      setTotalPages(result.data.total_pages || 0);
    }

    fetchBills();
  }, [currentPage, currentPageLimit]); // Fetch data when page or page limit changes

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageLimitChange = (newLimit) => {
    setCurrentPageLimit(newLimit);
  };

  const handleAddBill = async (e) => {
    e.preventDefault();
    const billData = {
      biller_id: formData.biller_id,
      biller: formData.biller,
      bill_type: formData.bill_type,
      bill_payment_type: formData.bill_payment_type,
      amount: formData.amount,
      due_date: formData.due_date,
      platform: formData.platform,
      store: formData.store,
      remarks: formData.remarks,
      status: formData.status || 'active',
      payment_frequency: formData.payment_frequency || 'one_time',
    };
    if (formData.store === '') {
      delete billData.store;
    }
    // A monthly bill can be created with its whole run of due cycles: the
    // backend generates `due_months` payables from due_date. Only sent for
    // monthly bills — a one-time bill always gets exactly one cycle.
    if (formData.payment_frequency === 'monthly' && formData.due_months !== '') {
      const months = parseInt(formData.due_months, 10);
      if (Number.isInteger(months) && months > 0) {
        billData.due_months = months;
      }
    }

    try {
      const response = await fetch(`${API_URL}/bills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(billData),
      });
      const result = await response.json();

      if (response.ok) {
        handleFlashMessage(result.message);
        refreshData();
        setTimeout(() => navigate("/bills"), 2000);
      } else {
        const errorDetail = result.response?.detail || 'Something went wrong';
        console.error('Error:', errorDetail);
        handleFlashMessage(`Error: ${errorDetail}`);
      }
    } catch (error) {
      console.error('Error adding bill:', error);
      handleFlashMessage('An error occurred while adding the bill.');
    }

    setFormData({
      biller_id: '',
      biller: '',
      bill_type: '',
      bill_payment_type: '',
      amount: '',
      due_date: '',
      platform: '',
      store: '',
      remarks: '',
      status: 'active',
      payment_frequency: 'one_time',
      due_months: '',
    });
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleDelete = async (billId) => {
    const response = await apiService.deleteBill(billId);
    try {
      handleFlashMessage(response.data.message + " Refreshing the page.");
      setRefreshKey(prevKey => prevKey + 1);
    } catch (error) {
      console.error(error);
      handleFlashMessage(response.detail + " Refreshing the page.");
    }
  };

  return (
    <PageContainer>
      <ContentContainer isSidebarOpen={isSidebarOpen}>
        <Header>
          <h1>Manage Bills</h1>
          <AddButton onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add New Bill'}
          </AddButton>
        </Header>
        <div>
          <Label>Total Bills: {totalItems}</Label>
          <Label>Total Pages: {totalPages}</Label>
        </div>
        {flashMessage && <FlashMessage message={flashMessage} />}
        <BillsTable
          refreshKey={refreshKey}
          setRefreshKey={setRefreshKey}
          handleFlashMessage={handleFlashMessage}
          handleDelete={handleDelete}
          currentPage={currentPage}
          currentPageLimit={currentPageLimit}
          onPageChange={handlePageChange}
          onPageLimitChange={handlePageLimitChange}
          onViewPayments={(payment, bill) => {
            setSelectedBill(bill);
          }}
        />
        {selectedBill && (
          <PaymentsModal
            bill={selectedBill}
            onClose={() => setSelectedBill(null)}
            onRefresh={refreshData}
          />
        )}
        {showForm && (
          <FormWrapper onSubmit={handleAddBill}>
            <h3>Add New Bill*</h3>
            <FormRow>
              <label htmlFor="biller">Biller*</label>
              <BillersDropdown
                value={formData.biller_id}
                onChange={(id, name) => {
                  setFormData(prev => ({
                    ...prev,
                    biller_id: id,
                    biller: name
                  }));
                }}
              />
            </FormRow>
            <FormRow>
              <label htmlFor="bill_type">Bill Type*</label>
              <BillTypeDropdown
                value={formData.bill_type}
                onChange={(e) => handleChange(e)}
              />
            </FormRow>
            <FormRow>
              <label htmlFor="bill_payment_type">Bill Payment Type*</label>
              <BillPaymentTypesDropdown
                value={formData.bill_payment_type}
                onChange={(e) => handleChange(e)}
              />
            </FormRow>
            <FormRow>
              <label htmlFor="amount">Amount*</label>
              <input
                id="amount"
                name="amount"
                type='number'
                value={formData.amount || ''}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor='due_date'>Due Date</Label>
              <DateInputField
                name='due_date'
                value={formData.due_date}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <label htmlFor="platform">Platform*</label>
              <input
                id="platform"
                name="platform"
                value={formData.platform || ''}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <label htmlFor="store">Store</label>
              <input
                id="store"
                name="store"
                value={formData.store || ''}
                onChange={handleChange}
              />
            </FormRow>
            <FormRow>
              <Label htmlFor="status">Status</Label>
              <StatusSelect
                id="status"
                name="status"
                value={formData.status || 'active'}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </StatusSelect>
            </FormRow>
            <FormRow>
              <Label htmlFor="payment_frequency">Payment frequency</Label>
              <StatusSelect
                id="payment_frequency"
                name="payment_frequency"
                value={formData.payment_frequency || 'one_time'}
                onChange={handleChange}
              >
                <option value="one_time">One-time</option>
                <option value="monthly">Monthly</option>
              </StatusSelect>
            </FormRow>
            {formData.payment_frequency === 'monthly' && (
              <FormRow>
                <Label htmlFor="due_months">Number of months</Label>
                <BillNumberInput
                  id="due_months"
                  name="due_months"
                  min="1"
                  max="120"
                  step="1"
                  value={formData.due_months || ''}
                  onChange={handleChange}
                />
                <HelperText>
                  How many monthly due cycles to create from the due date. Leave blank to
                  add them later from the bill's Payables view.
                </HelperText>
              </FormRow>
            )}
            <FormRow>
              <label htmlFor="remarks">Remarks</label>
              <textarea
                id="remarks"
                name="remarks"
                value={formData.remarks || ''}
                onChange={handleChange}
              />
            </FormRow>
            <SubmitButton type="submit">Save</SubmitButton>
          </FormWrapper>
        )}
      </ContentContainer>
    </PageContainer>
  );
};

export default Bills;
